import { useState } from "react";
import { useSignOut } from "@/hooks/useSignOut";
import { changeMyPassword, getMe, isApiError } from "@safers/api";
import { useAuthStore, selectUser } from "@safers/auth";
import { Bell, ChevronDown, ExternalLink, Lock } from "@safers/ui/atoms";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@safers/ui/molecules";
import {
  PasswordChangeCard,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalBody,
} from "@safers/ui/organisms";
import { useToastStore } from "@/stores";
import { useAppSetup } from "@/hooks/useAppSetup";

const isPasswordChangeRejected = (err: unknown) => isApiError(err) && !err.isServerError;
const PW_MIN_LENGTH = 8;

type PasswordChangeErrors = Partial<
  Record<"currentPassword" | "newPassword" | "confirmPassword", string>
>;

function validatePassword(
  currentPassword: string,
  newPassword: string,
  confirmPassword: string
): PasswordChangeErrors {
  const next: PasswordChangeErrors = {};
  if (!currentPassword) next.currentPassword = "필수입니다.";
  if (!newPassword) next.newPassword = "필수입니다.";
  else if (newPassword.length < PW_MIN_LENGTH)
    next.newPassword = `${PW_MIN_LENGTH}자 이상 입력하세요.`;
  if (!confirmPassword) next.confirmPassword = "필수입니다.";
  else if (newPassword !== confirmPassword) next.confirmPassword = "비밀번호가 일치하지 않습니다.";
  return next;
}

export function HeaderUserInfo({ tone = "light" }: { tone?: "light" | "dark" }) {
  const setup = useAppSetup();
  const user = useAuthStore(selectUser);
  const [pwModalOpen, setPwModalOpen] = useState(false);
  const [pwSubmitting, setPwSubmitting] = useState(false);
  const [pwPasswords, setPwPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [pwErrors, setPwErrors] = useState<PasswordChangeErrors>({});
  const handleLogout = useSignOut();

  if (!user) return null;

  const dark = tone === "dark";

  const authority = user.authority ?? "USER";
  const isAdmin = authority === "ADMIN";
  const showAdminEntry = isAdmin;

  const initials = (user.name ?? user.username ?? "?").slice(0, 2).toUpperCase();

  function handlePwModalOpenChange(next: boolean) {
    setPwModalOpen(next);
    if (!next) {
      setPwErrors({});
      setPwPasswords({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  }

  function clearFieldError(field: "currentPassword" | "newPassword" | "confirmPassword") {
    setPwErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }

  async function handlePasswordSubmit(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string
  ) {
    const next = validatePassword(currentPassword, newPassword, confirmPassword);
    setPwErrors(next);
    if (Object.keys(next).length > 0) return;

    setPwSubmitting(true);
    try {
      await changeMyPassword({
        currentPassword,
        newPassword,
      });
      useToastStore.getState().toast.success("비밀번호가 변경되었습니다.");
      getMe()
        .then((me) => useAuthStore.getState().setUser(me))
        .catch(() => {});
      handlePwModalOpenChange(false);
    } catch (err) {
      if (isPasswordChangeRejected(err)) {
        setPwErrors({
          currentPassword: "현재 비밀번호가 올바르지 않습니다.",
        });
      } else {
        useToastStore.getState().toast.error("비밀번호 변경에 실패했습니다.");
      }
    } finally {
      setPwSubmitting(false);
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={`flex items-center gap-2.5 rounded-md px-2 py-1 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand ${
              dark ? "hover:bg-white/10" : "hover:bg-neutral-100"
            }`}
          >
            <span
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold"
              style={{
                background: "var(--brand-primary-soft)",
                color: "var(--brand-primary-on-soft)",
              }}
            >
              {initials}
            </span>
            <span className="hidden flex-col items-start leading-tight sm:flex">
              <span className={`text-sm font-medium ${dark ? "text-white" : "text-primary"}`}>
                {user.name ?? user.username}
              </span>
              <span className={`text-xs uppercase ${dark ? "text-white/60" : "text-tertiary"}`}>
                {authority}
              </span>
            </span>
            <ChevronDown
              size="sm"
              className={`hidden sm:block ${dark ? "text-white/60" : "text-tertiary"}`}
            />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          sideOffset={6}
          collisionPadding={8}
          className="min-w-[13.75rem] p-0"
        >
          <div className="border-b border-[var(--border-subtle)] px-3 py-2.5">
            <div className="text-sm font-semibold text-primary dark:text-dark-text-primary">
              {user.name ?? "—"}
            </div>
            <div className="font-mono text-xs text-tertiary dark:text-dark-text-muted">
              {user.username}
            </div>
          </div>

          <div className="p-1">
            {showAdminEntry && (
              <>
                <DropdownMenuItem asChild>
                  <a
                    href="/admin/"
                    className="flex items-center gap-2 px-2 py-1.5 text-sm font-semibold text-brand"
                  >
                    <ExternalLink size="sm" />
                    관리자 페이지
                  </a>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {/* 배너를 다시 띄우는 메뉴라, 배너를 띄우지 않는 PC 에서는 뺀다. */}
            {setup.notification.state !== "on" && setup.env.os !== "desktop" && (
              <DropdownMenuItem
                onClick={setup.reset}
                className="flex items-center gap-2 px-2 py-1.5 text-sm"
              >
                <Bell size="sm" />
                알림 설정
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => setPwModalOpen(true)}
              className="flex items-center gap-2 px-2 py-1.5 text-sm"
            >
              <Lock size="sm" />
              비밀번호 변경
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              className="px-2 py-1.5 text-sm text-error-brand"
            >
              로그아웃
            </DropdownMenuItem>
          </div>

          <div className="border-t border-[var(--border-subtle)] px-3 py-1.5 text-right font-mono text-[0.6875rem] text-tertiary dark:text-dark-text-muted">
            v{import.meta.env.VITE_PACKAGE_VERSION}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <Modal open={pwModalOpen} onOpenChange={handlePwModalOpenChange}>
        <ModalContent className="max-w-md">
          <ModalHeader>
            <ModalTitle>비밀번호 변경</ModalTitle>
            <ModalDescription>현재 비밀번호 확인 후 새 비밀번호로 변경합니다.</ModalDescription>
          </ModalHeader>
          <ModalBody className="pt-4">
            <PasswordChangeCard
              values={pwPasswords}
              onChange={(field, value) => {
                setPwPasswords((prev) => ({ ...prev, [field]: value }));
                clearFieldError(field);
              }}
              onSubmit={handlePasswordSubmit}
              isLoading={pwSubmitting}
              errors={pwErrors}
              newPasswordPlaceholder={`${PW_MIN_LENGTH}자 이상 입력`}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
